import * as React from "react";

import { cn } from "@/lib/utils";
import { NewLink } from "../new-link";
import { Link as RRLink } from "react-router";
import { useFlags } from "launchdarkly-react-client-sdk";
import { FeatureFlag } from "@/constants";
const HeadingPageTitle = ({
    className,
    as,
    ...props
}: React.HTMLAttributes<HTMLHeadingElement> & {
    as?: React.ElementType;
}) => {
    const Component = as ?? "h1";
    const powerMove = useFlags()[FeatureFlag.IL_4194_POWER_MOVE];

    return (
        <Component
            className={cn(
                "font-heading text-enormous font-boldest leading-largest tracking-tightest text-default-color",
                powerMove && "font-bold",
                className,
            )}
            {...props}
        />
    );
};
HeadingPageTitle.displayName = "HeadingPageTitle";

const HeadingSection = ({
    className,
    as,
    ...props
}: React.HTMLAttributes<HTMLHeadingElement> & {
    as?: React.ElementType;
}) => {
    const Component = as ?? "h2";
    const powerMove = useFlags()[FeatureFlag.IL_4194_POWER_MOVE];
    return (
        <Component
            className={cn(
                "font-heading text-giant font-boldest leading-larger text-default-color tracking-tighter",
                powerMove && "font-bold",
                className,
            )}
            {...props}
        />
    );
};
HeadingSection.displayName = "HeadingSection";

const HeadingCard = ({
    className,
    as,
    ...props
}: React.HTMLAttributes<HTMLHeadingElement> & {
    as?: React.ElementType;
}) => {
    const Component = as ?? "h3";
    const powerMove = useFlags()[FeatureFlag.IL_4194_POWER_MOVE];
    return (
        <Component
            className={cn(
                "font-heading text-largest font-boldest leading-large text-default-color tracking-tight",
                powerMove && "font-bold",
                className,
            )}
            {...props}
        />
    );
};
HeadingCard.displayName = "HeadingCard";

const HeadingSubsection = ({
    className,
    as,
    ...props
}: React.HTMLAttributes<HTMLHeadingElement> & {
    as?: React.ElementType;
}) => {
    const Component = as ?? "h4";
    const powerMove = useFlags()[FeatureFlag.IL_4194_POWER_MOVE];
    return (
        <Component
            className={cn(
                "font-heading text-larger font-boldest leading-default tracking-default text-default-color",
                powerMove && "font-bold",
                className,
            )}
            {...props}
        />
    );
};
HeadingSubsection.displayName = "HeadingSubsection";

const HeadingElementTitle = ({
    className,
    as,
    ...props
}: React.HTMLAttributes<HTMLHeadingElement> & {
    as?: React.ElementType;
}) => {
    const Component = as ?? "h5";
    const powerMove = useFlags()[FeatureFlag.IL_4194_POWER_MOVE];
    return (
        <Component
            className={cn(
                "font-body text-large font-bolder leading-default text-default-color",
                powerMove && "font-default",
                className,
            )}
            {...props}
        />
    );
};
HeadingElementTitle.displayName = "HeadingElementTitle";

const BodyDefault = ({
    className,
    as,
    ...props
}: React.HTMLAttributes<HTMLParagraphElement> & {
    as?: React.ElementType;
}) => {
    const Component = as ?? "p";
    return (
        <Component
            className={cn(
                "font-body text-default font-default leading-default text-default-color",
                className,
            )}
            {...props}
        />
    );
};
BodyDefault.displayName = "BodyDefault";

const BodyBold = ({
    className,
    as,
    ...props
}: React.HTMLAttributes<HTMLParagraphElement> & {
    as?: React.ElementType;
}) => {
    const Component = as ?? "p";
    return (
        <Component
            className={cn(
                "font-body text-default font-bolder leading-default text-default-color",
                className,
            )}
            {...props}
        />
    );
};
BodyBold.displayName = "BodyBold";

const BodySmall = ({
    className,
    as,
    ...props
}: React.HTMLAttributes<HTMLParagraphElement> & {
    as?: React.ElementType;
}) => {
    const Component = as ?? "p";
    return (
        <Component
            className={cn(
                "font-body text-small font-default leading-small text-default-color",
                className,
            )}
            {...props}
        />
    );
};
BodySmall.displayName = "BodySmall";

const BodyLabel = ({
    className,
    as,
    ...props
}: React.HTMLAttributes<HTMLParagraphElement> & {
    as?: React.ElementType;
}) => {
    const Component = as ?? "p";
    return (
        <Component
            className={cn(
                "font-body text-default leading-default text-default-color font-bold",
                className,
            )}
            {...props}
        />
    );
};
BodyLabel.displayName = "BodyLabel";

const BodyLabelSmall = ({
    className,
    as,
    ...props
}: React.HTMLAttributes<HTMLParagraphElement> & {
    as?: React.ElementType;
}) => {
    const Component = as ?? "p";
    return (
        <Component
            className={cn(
                "font-body text-small leading-small text-default-color font-bold",
                className,
            )}
            {...props}
        />
    );
};
BodyLabelSmall.displayName = "BodyLabelSmall";

const BodyCode = ({
    className,
    as,
    ...props
}: React.HTMLAttributes<HTMLParagraphElement> & {
    as?: React.ElementType;
}) => {
    const Component = as ?? "code";
    return (
        <Component
            className={cn(
                "font-code text-default font-default leading-default text-default-color",
                className,
            )}
            {...props}
        />
    );
};
BodyCode.displayName = "BodyCode";

const Link = ({
    withNewLink = false,
    isTruncated = false,
    className,
    ...props
}: React.ComponentProps<typeof NewLink> & {
    withNewLink?: boolean;
    isTruncated?: boolean;
}) => {
    const Comp = withNewLink ? NewLink : RRLink;
    return (
        <Comp
            className={cn(
                "text-link-default hover:text-link-hover active:text-link-press cursor-pointer hover:underline",
                isTruncated && "block max-w-[50ch] truncate",
                className,
            )}
            {...props}
        />
    );
};
Link.displayName = "Link";

export {
    HeadingPageTitle,
    HeadingSection,
    HeadingCard,
    HeadingSubsection,
    HeadingElementTitle,
    BodyDefault,
    BodyBold,
    BodySmall,
    BodyLabel,
    BodyLabelSmall,
    BodyCode,
    Link,
};
